import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AdminPageLayout from "../../../components/AdminPageLayout/AdminPageLayout";
import { Headings } from "../../../components/Headings";
import LoadingMask from "../../../components/LoadingMask/LoadingMask";
import { useAppAuthState } from "../../../hooks/useAppAuthState";
import { getUserActivities } from "../../../services/activityLogger";
import { UserActivity } from "../../../types/User";
import "./AdminPage.scss";

const AdminPage: React.FC = () => {
  const { user, isUserAdmin } = useAppAuthState();
  const [activities, setActivities] = useState<UserActivity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchActivities = async () => {
      setIsLoading(true);

      try {
        const fetchedActivities = await getUserActivities();

        setActivities(fetchedActivities);
        setError(null);
      } catch (err: any) {
        console.error("Error in AdminPage useEffect:", err);
        setError(
          err.message || "Failed to fetch activities or verify admin status."
        );
      } finally {
        setIsLoading(false);
      }
    };

    if (isUserAdmin) {
      fetchActivities();
    }
  }, [isUserAdmin, user]);

  const pageContent = useMemo(() => {
    if (isLoading) {
      return <LoadingMask />;
    }

    if (isUserAdmin === false) {
      return (
        <div className="admin-page-content">
          <div className="alert alert-danger">
            {error || "Access Denied. You must be an admin to view this page."}
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="admin-page-content">
          <div className="alert alert-danger">{error}</div>
        </div>
      );
    }

    return (
      <div className="admin-page-content">
        <div className="quick-links">
          <Link to="/admin/users">User Management</Link>
          <Link to="/admin/places">Place Management</Link>
          <Link to="/admin/data-submissions">Data Submissions</Link>
        </div>
        <div className="admin-sections">
          <div className="admin-section user-activity-log">
            <Headings.Section>User Activity Log</Headings.Section>
            {activities.length === 0 && (
              <div className="alert alert-info">No user activities found.</div>
            )}
            {activities.length > 0 && (
              <table className="table activity-table">
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>User ID</th>
                    <th>Event Type</th>
                    <th>Context</th>
                  </tr>
                </thead>
                <tbody>
                  {activities.map((activity) => (
                    <tr key={activity.id}>
                      <td>{new Date(activity.timestamp).toLocaleString()}</td>
                      <td>{activity.userId}</td>
                      <td>{activity.eventType}</td>
                      <td>
                        {activity.context
                          ? JSON.stringify(activity.context)
                          : "N/A"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    );
  }, [isLoading, isUserAdmin, error, activities]);

  return <AdminPageLayout title="Dashboard">{pageContent}</AdminPageLayout>;
};

export default AdminPage;
